import { type QueryKey, useInfiniteQuery, UseInfiniteQueryOptions } from '@tanstack/react-query';
import { useEffect } from 'react';

import { AuditTrailService } from '../services/api';
import { useCloudPlatformDiscovery } from '../utils/hooks';
import type {
  SearchEventsRequest,
  SearchEventsRequest_Filter,
  SearchEventsResponse,
} from './gen/ai/h2o/audittrail/v1/event_service_pb';

export const useAuditTrailService = () => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const auditTrailUrl = cloudPlatformDiscovery?.auditTrailApiUrl || '';

  useEffect(() => {
    AuditTrailService.setBasePath(auditTrailUrl);
  }, [auditTrailUrl]);

  return AuditTrailService;
};

export const cacheKeys = {
  auditTrailAll: (params?: Pick<SearchEventsRequest, 'filter'>) => {
    const baseKey: QueryKey = ['auditTrail', 'events', 'all'];
    if (!params) return baseKey;
    const { filter } = params;
    // this converts each filter field to a zero-value so filter requests with different empty-values are equivalent in cache
    const filterWithDefaults: SearchEventsRequest_Filter = {
      startEventTime: filter?.startEventTime || '',
      endEventTime: filter?.endEventTime || '',
      eventSourceExact: filter?.eventSourceExact || '',
      eventSourceRegex: filter?.eventSourceRegex || '',
      resourceExact: filter?.resourceExact || '',
      resourceRegex: filter?.resourceRegex || '',
      readOnly: filter?.readOnly !== undefined ? filter.readOnly : undefined,
      statusCodeRegex: filter?.statusCodeRegex || '',
      principalExact: filter?.principalExact || '',
      principalRegex: filter?.principalRegex || '',
      sourceIpAddressExact: filter?.sourceIpAddressExact || '',
      sourceIpAddressRegex: filter?.sourceIpAddressRegex || '',
      metadataExact: filter?.metadataExact || undefined,
      metadataRegex: filter?.metadataRegex || undefined,
      workspaceExact: filter?.workspaceExact || '',
      workspaceRegex: filter?.workspaceRegex || '',
      actionExact: filter?.actionExact || '',
      actionRegex: filter?.actionRegex || '',
    };
    return [...baseKey, { filter: filterWithDefaults }];
  },
};

export const useQueryAuditTrail = <TData = SearchEventsResponse>(
  request: Omit<SearchEventsRequest, 'pageToken'> = {},
  options?: Omit<
    UseInfiniteQueryOptions<SearchEventsResponse, unknown, TData>,
    'getNextPageParam' | 'queryKey' | 'queryFn'
  >
) => {
  const auditTrailService = useAuditTrailService();

  return useInfiniteQuery({
    ...options,
    queryKey: cacheKeys.auditTrailAll(request),
    queryFn: async ({ pageParam, signal }) => {
      try {
        return await auditTrailService.searchEvents({ ...request, pageToken: pageParam }, { signal });
      } catch (err) {
        throw err;
      }
    },
    getNextPageParam: (lastPage) => {
      const nextPageToken = lastPage.nextPageToken;

      if (!nextPageToken || nextPageToken === '') return undefined;

      return nextPageToken;
    },
  });
};
